import { UrlEventType, UrlParameter, VisitType } from 'src/requester';
import {
  IKameleoonData,
  KameleoonData,
  PageViewDataType,
  PageViewParametersType,
  TimestampType,
} from './types';
import { TrackingStatus } from 'src/types';

/**
 * @class
 * PageView - a class for creating an instance of a page view data
 * */
export class PageView implements IKameleoonData {
  // --- Note ---
  // Kept in ascending order
  public timestamps: TimestampType[];

  private urlAddress: string;
  private title: string;
  private nonce?: string;
  private referrers?: number[];

  /**
   * @param {PageViewParametersType} pageViewParameters - an object with page view parameters
   * */
  constructor({ urlAddress, title, referrers }: PageViewParametersType) {
    this.urlAddress = urlAddress;
    this.title = title;
    this.referrers = referrers;
    this.timestamps = [
      {
        time: Date.now(),
        status: TrackingStatus.Unsent,
      },
    ];
  }

  get url(): string {
    // --- Note ---
    // If SDK is used in vanilla JS codebase, then you're also able to create an instance
    // with no required data, we don't want send anything to tracking in that case
    if (!this.urlAddress) {
      return '';
    }

    const titleParameter = this.title
      ? UrlParameter.Title + encodeURIComponent(this.title)
      : '';

    const referrersParameter = this.referrers?.length
      ? UrlParameter.ReferrersIndices + JSON.stringify(this.referrers)
      : '';

    return (
      UrlEventType.Page +
      UrlParameter.Href +
      encodeURIComponent(this.urlAddress) +
      titleParameter +
      referrersParameter
    );
  }

  get status(): TrackingStatus {
    const {
      [TrackingStatus.Sent]: sent,
      [TrackingStatus.Unsent]: unsent,
      [TrackingStatus.Pending]: pending,
    } = this.timestamps.reduce(
      (acc, { status }) => {
        acc[status] = true;
        return acc;
      },
      {
        [TrackingStatus.Sent]: false,
        [TrackingStatus.Unsent]: false,
        [TrackingStatus.Pending]: false,
      },
    );

    if (sent && !unsent && !pending) {
      return TrackingStatus.Sent;
    }

    if (pending && !unsent) {
      return TrackingStatus.Pending;
    }

    return TrackingStatus.Unsent;
  }

  set status(status: TrackingStatus) {
    this.timestamps = this.timestamps.map((timestamp) => ({
      ...timestamp,
      status,
    }));
  }

  set _nonce(nonce: string) {
    this.nonce = nonce;
  }

  get data(): PageViewDataType {
    return {
      urlAddress: this.urlAddress,
      title: this.title,
      referrers: this.referrers,
      type: KameleoonData.PageView,
      timestamps: this.timestamps,
      status: this.status,
      nonce: this.nonce,
    };
  }

  /**
   * @private
   * @method _fromRaw - an internal method for creating a PageView instance from raw data
   * @param {PageViewDataType} data - a raw data
   * @return {PageView} a PageView instance
   * */
  public static _fromRaw(data: PageViewDataType): PageView {
    const { urlAddress, title, referrers, timestamps, nonce } = data;
    const pageView = new PageView({
      urlAddress,
      title,
      referrers,
    });

    pageView.timestamps = timestamps;

    if (nonce) {
      pageView._nonce = nonce;
    }

    return pageView;
  }

  /**
   * @private
   * @method _updateFromVisit - an internal method for updating a list of page view data from a visit
   * @param {VisitType} visit - a visit
   * @param {Map<string, PageView>} dataMap - a map of page view data where we collect data
   * */
  public static _updateFromVisit(
    visit: VisitType,
    dataMap: Map<string, PageView>,
  ): void {
    const pageEvents = visit.pageEvents;

    if (pageEvents) {
      for (const pageEvent of pageEvents) {
        let referrers: number[] = [];

        const { time } = pageEvent;
        const { href, title, referrersIndices } = pageEvent.data;

        if (typeof referrersIndices[0] === 'number') {
          referrers = referrersIndices as number[];
        }

        let pageView: PageView;

        if (dataMap.has(href)) {
          const existingTimestamps = dataMap.get(href)!.timestamps;

          pageView = new PageView({
            urlAddress: href,
            title,
            referrers,
          });

          pageView.timestamps = [
            ...existingTimestamps,
            { time, status: TrackingStatus.Sent },
          ];
        } else {
          pageView = new PageView({
            urlAddress: href,
            title,
            referrers,
          });

          pageView.timestamps = [{ time, status: TrackingStatus.Sent }];
        }

        dataMap.set(href, pageView);
      }
    }
  }
}
